import { useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronUp, Loader2, Sparkles } from "lucide-react";
import { TOOL_COLORS, TOOL_CATEGORY_MAP, CATEGORY_LABELS, CATEGORY_BADGE_COLORS, TOOLS_BY_CATEGORY } from "@/lib/toolConstants";

export interface ToolRecommendation {
  rank: number;
  tool: string;
  model: string;
  reasoning: string;
}

interface ToolRecommendationCardsProps {
  recommendations: ToolRecommendation[];
  onSelect: (tool: ToolRecommendation) => void;
  onSkip?: () => void;
  isOptimizing: boolean;
}

const RANK_LABELS = ["🥇 Best", "🥈 Better", "🥉 Good"];

const ToolRecommendationCards = ({
  recommendations,
  onSelect,
  onSkip,
  isOptimizing,
}: ToolRecommendationCardsProps) => {
  const [selectedTool, setSelectedTool] = useState<ToolRecommendation | null>(null);
  const [showAllTools, setShowAllTools] = useState(false);

  const recommendedToolNames = new Set(recommendations.map((r) => r.tool));

  const handleCardClick = (rec: ToolRecommendation) => {
    if (isOptimizing) return;
    setSelectedTool(rec);
    setShowAllTools(false);
  };

  const handlePickTool = (toolName: string) => {
    if (isOptimizing) return;
    const custom: ToolRecommendation = {
      rank: 0,
      tool: toolName,
      model: "",
      reasoning: "User selected manually",
    };
    setSelectedTool(custom);
    setShowAllTools(false);
  };

  const handleOptimize = () => {
    if (selectedTool && !isOptimizing) {
      onSelect(selectedTool);
    }
  };

  return (
    <div className="space-y-3 fade-in-up">
      <div className="grid gap-2">
        {recommendations.map((rec, i) => {
          const isSelected = selectedTool?.tool === rec.tool && selectedTool?.rank === rec.rank;
          const colorClass = TOOL_COLORS[rec.tool] || "border-border bg-muted/20";
          const category = TOOL_CATEGORY_MAP[rec.tool];
          const categoryLabel = category ? CATEGORY_LABELS[category] : null;
          const badgeColor = category ? CATEGORY_BADGE_COLORS[category] : null;

          return (
            <button
              key={rec.rank}
              onClick={() => handleCardClick(rec)}
              disabled={isOptimizing}
              className={cn(
                "w-full text-left rounded-xl border-2 px-4 py-3 transition-all duration-200",
                colorClass,
                isSelected
                  ? "ring-2 ring-primary shadow-md scale-[1.01]"
                  : "hover:shadow-sm hover:scale-[1.005]",
                isOptimizing && "opacity-60 cursor-not-allowed"
              )}
            >
              <div className="flex items-start gap-3">
                <span className="text-lg flex-shrink-0 mt-0.5">{RANK_LABELS[i]}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-foreground">{rec.tool}</span>
                    {categoryLabel && badgeColor && category !== "LLM" && (
                      <span className={cn("text-[10px] font-medium px-1.5 py-0.5 rounded-full", badgeColor)}>
                        {categoryLabel}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                      {rec.model}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {rec.reasoning}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {selectedTool && (
        <Button
          onClick={handleOptimize}
          disabled={isOptimizing}
          className="w-full"
          size="lg"
        >
          {isOptimizing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Optimizing for {selectedTool.tool}...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 mr-2" />
              Optimize for {selectedTool.tool}
            </>
          )}
        </Button>
      )}

      {!isOptimizing && (
        <div className="space-y-2">
          <button
            onClick={() => setShowAllTools((prev) => !prev)}
            className="w-full flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {showAllTools ? (
              <>
                <ChevronUp className="h-3.5 w-3.5" />
                Hide other tools
              </>
            ) : (
              <>
                <ChevronDown className="h-3.5 w-3.5" />
                Skip or select a different tool
              </>
            )}
          </button>

          {showAllTools && (
            <div className="rounded-xl border border-border bg-card p-3 space-y-3 max-h-64 overflow-y-auto">
              {Object.entries(TOOLS_BY_CATEGORY).map(([category, tools]) => {
                const availableTools = tools.filter((t) => !recommendedToolNames.has(t));
                if (availableTools.length === 0) return null;

                return (
                  <div key={category}>
                    <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
                      {category}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {availableTools.map((tool) => {
                        const isPickedManual = selectedTool?.tool === tool && selectedTool?.rank === 0;
                        const toolColor = TOOL_COLORS[tool] || "border-border bg-muted/20";
                        return (
                          <button
                            key={tool}
                            onClick={() => handlePickTool(tool)}
                            className={cn(
                              "text-xs px-2.5 py-1.5 rounded-lg border transition-all",
                              toolColor,
                              isPickedManual
                                ? "ring-2 ring-primary shadow-sm font-semibold"
                                : "hover:shadow-sm"
                            )}
                          >
                            {tool}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {onSkip && (
                <button
                  onClick={onSkip}
                  className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors pt-2 border-t border-border"
                >
                  Skip – I'm using a different model
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ToolRecommendationCards;
